import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Beaker, RefreshCw, FlaskConical, Plus, AlertTriangle } from "lucide-react";

const SPORT_KEYS = [
  { key: "basketball_nba", label: "NBA", code: "NBA" },
  { key: "americanfootball_nfl", label: "NFL", code: "NFL" },
  { key: "baseball_mlb", label: "MLB", code: "MLB" },
  { key: "icehockey_nhl", label: "NHL", code: "NHL" },
  { key: "soccer_epl", label: "Soccer (EPL)", code: "SOCCER" },
];

const MARKET_PRESETS: Record<string, string> = {
  core: "h2h,spreads,totals",
  player_props_nba: "player_points,player_rebounds,player_assists",
  player_props_nfl: "player_pass_yds,player_rush_yds,player_receptions",
  player_props_mlb: "batter_hits,pitcher_strikeouts",
};

type ApiResp = {
  success: boolean;
  status: number;
  duration_ms: number;
  quota?: { remaining: string | null; used: string | null };
  error?: string | null;
  data: any;
};

export default function ApiTestLab({ sports }: { sports: { id: string; code: string }[] }) {
  const [sportKey, setSportKey] = useState("basketball_nba");
  const [marketPreset, setMarketPreset] = useState("core");
  const [customMarkets, setCustomMarkets] = useState("");
  const [eventId, setEventId] = useState("");
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState<ApiResp | null>(null);
  const [logs, setLogs] = useState<any[]>([]);

  async function loadLogs() {
    const { data } = await supabase
      .from("api_usage_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(25);
    setLogs(data ?? []);
  }
  useEffect(() => { loadLogs(); }, []);

  async function call(action: "sports" | "events" | "odds" | "event_odds") {
    setBusy(true);
    setResp(null);
    try {
      const markets = customMarkets.trim() || MARKET_PRESETS[marketPreset];
      const { data, error } = await supabase.functions.invoke("odds_api_test", {
        body: { action, sport: sportKey, markets, eventId: eventId || undefined },
      });
      if (error) throw error;
      setResp(data as ApiResp);
      if (!(data as ApiResp).success) toast.error((data as ApiResp).error ?? "API error");
      else toast.success(`OK in ${(data as ApiResp).duration_ms}ms`);
    } catch (e: any) {
      toast.error(e.message ?? "Call failed");
    } finally {
      setBusy(false);
      loadLogs();
    }
  }

  async function createDraftFromGame(g: any) {
    const sportCode = SPORT_KEYS.find((s) => s.key === sportKey)?.code ?? "NBA";
    const sport_id = sports.find((s) => s.code === sportCode)?.id;
    if (!sport_id) return toast.error(`Sport ${sportCode} not found in DB`);

    // Try to extract a moneyline/spread/total from bookmakers if present
    const bm = g.bookmakers?.[0];
    const mkt = bm?.markets?.[0];
    const out = mkt?.outcomes?.[0];
    const teams = `${g.away_team} @ ${g.home_team}`;
    const title = `${teams} — ${mkt?.key ?? "TBD"}${out?.name ? ` (${out.name})` : ""}`;

    const { error } = await supabase.from("picks").insert({
      sport_id,
      bet_type: mkt?.key === "spreads" ? "spread" : mkt?.key === "totals" ? "game_total" : "moneyline",
      title,
      teams,
      line: out?.point ?? null,
      odds: out?.price ?? null,
      sportsbook: bm?.title ?? null,
      confidence_score: 50,
      unit_rating: 1,
      risk: "medium",
      status: "draft",
      ai_summary: `Imported from The Odds API test lab (event ${g.id ?? ""}).`,
      game_start_time: g.commence_time ?? null,
    });
    if (error) return toast.error(error.message);
    window.dispatchEvent(new Event("picks:refresh"));
    toast.success("Draft pick created — see it in the Picks list above (status: draft).");
  }

  const data = resp?.data;
  const isList = Array.isArray(data);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <FlaskConical className="w-5 h-5 text-accent" />
          API Test Lab — The Odds API
          <Badge variant="outline" className="ml-2">Admin only</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
          <AlertTriangle className="w-3.5 h-3.5" />
          Sandbox for testing live odds ingestion. Results never auto-publish; create drafts only.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Sport</Label>
            <Select value={sportKey} onValueChange={setSportKey}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SPORT_KEYS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Market preset</Label>
            <Select value={marketPreset} onValueChange={setMarketPreset}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="core">Moneyline / Spread / Total</SelectItem>
                <SelectItem value="player_props_nba">Player props — NBA</SelectItem>
                <SelectItem value="player_props_nfl">Player props — NFL</SelectItem>
                <SelectItem value="player_props_mlb">Player props — MLB</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Custom markets (optional)</Label>
            <Input value={customMarkets} onChange={(e) => setCustomMarkets(e.target.value)} placeholder="h2h,spreads,totals" />
          </div>
          <div>
            <Label className="text-xs">Event ID (for player props)</Label>
            <Input value={eventId} onChange={(e) => setEventId(e.target.value)} placeholder="optional" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => call("sports")}>
            <Beaker className="w-3 h-3 mr-1" /> Test connection
          </Button>
          <Button size="sm" disabled={busy} onClick={() => call("events")}>Fetch events</Button>
          <Button size="sm" disabled={busy} onClick={() => call("odds")}>Fetch odds (ML/Spread/Total)</Button>
          <Button size="sm" disabled={busy || !eventId} onClick={() => call("event_odds")}>Fetch event odds (props)</Button>
          <Button size="sm" variant="ghost" onClick={loadLogs}><RefreshCw className="w-3 h-3 mr-1" />Refresh logs</Button>
        </div>

        {resp && (
          <div className="rounded-lg border border-border bg-card/50 p-3 space-y-2">
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <Badge variant={resp.success ? "default" : "destructive"}>
                {resp.success ? "Success" : "Error"} · {resp.status}
              </Badge>
              <span className="text-muted-foreground">{resp.duration_ms} ms</span>
              {resp.quota?.remaining && <span className="text-muted-foreground">Quota remaining: {resp.quota.remaining}</span>}
              {resp.error && <span className="text-destructive">{resp.error}</span>}
            </div>

            <Tabs defaultValue="table">
              <TabsList>
                <TabsTrigger value="table">Readable</TabsTrigger>
                <TabsTrigger value="json">Raw JSON</TabsTrigger>
              </TabsList>
              <TabsContent value="table">
                {isList && data.length > 0 ? (
                  <div className="overflow-auto max-h-[420px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Game / Item</TableHead>
                          <TableHead>Start</TableHead>
                          <TableHead>Books</TableHead>
                          <TableHead>Sample market</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.slice(0, 50).map((g: any, i: number) => {
                          const bm = g.bookmakers?.[0];
                          const mkt = bm?.markets?.[0];
                          const matchup = g.home_team ? `${g.away_team} @ ${g.home_team}` : (g.title ?? g.key ?? JSON.stringify(g).slice(0, 60));
                          return (
                            <TableRow key={g.id ?? i}>
                              <TableCell className="max-w-[280px]">
                                <div className="font-medium truncate">{matchup}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{g.id}</div>
                              </TableCell>
                              <TableCell className="text-xs">{g.commence_time ? new Date(g.commence_time).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " PT" : "—"}</TableCell>
                              <TableCell className="text-xs">{g.bookmakers?.length ?? 0}</TableCell>
                              <TableCell className="text-xs">
                                {mkt ? `${mkt.key}: ${mkt.outcomes?.map((o: any) => `${o.name} ${o.price}${o.point != null ? ` @${o.point}` : ""}`).slice(0, 2).join(" / ")}` : "—"}
                              </TableCell>
                              <TableCell className="text-right">
                                {g.home_team && (
                                  <Button size="sm" variant="secondary" onClick={() => createDraftFromGame(g)}>
                                    <Plus className="w-3 h-3 mr-1" />Draft
                                  </Button>
                                )}
                                {g.id && !g.home_team === false && (
                                  <Button size="sm" variant="ghost" onClick={() => setEventId(g.id)}>Use ID</Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground py-4">No tabular data — see Raw JSON.</p>
                )}
              </TabsContent>
              <TabsContent value="json">
                <pre className="text-xs bg-background/60 p-3 rounded border border-border overflow-auto max-h-[420px]">
{JSON.stringify(data, null, 2)}
                </pre>
              </TabsContent>
            </Tabs>
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold mb-2">Recent API calls</h4>
          <div className="overflow-auto max-h-[260px] rounded border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Params</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>ms</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">No calls yet.</TableCell></TableRow>}
                {logs.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs">{new Date(l.created_at).toLocaleTimeString("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", minute: "2-digit", second: "2-digit" }) + " PT"}</TableCell>
                    <TableCell className="text-xs">{l.endpoint}</TableCell>
                    <TableCell className="text-xs max-w-[260px] truncate">{JSON.stringify(l.request_params)}</TableCell>
                    <TableCell>
                      <Badge variant={l.success ? "default" : "destructive"} className="text-[10px]">{l.status_code ?? "—"}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{l.duration_ms ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
